/**
 * 엑셀·구글시트·워드에서 복사한 표를 채팅 본문으로 다루는 순수 함수 모음.
 *
 * 저장 형식은 **탭 구분 평문**이다 — messages.content는 지금처럼 그냥 텍스트이고
 * DB·암호화·소켓 payload 계약을 하나도 건드리지 않는다. 표라는 사실은 마커가 아니라
 * 렌더 시점의 형태 판정으로 되살린다. 그래서 구버전 클라이언트나 알림 본문·채널 목록
 * 미리보기에서는 평문으로 보이는 것이 정상 폴백이다.
 *
 * ⚠ 붙여넣기와 렌더는 판정 함수 `looksLikeTable` **하나**를 공유한다. 이 대칭이 깨지면
 *   "붙여넣었는데 표로 안 보인다" / "타이핑한 게 표가 됐다" 두 버그가 동시에 생긴다.
 *
 * ⚠ 글과 표를 함께 복사한 클립보드에서 표만 뽑으면 본문이 사라진다 — extractClipboardPaste 참고.
 *
 * ⚠ 이 파일은 **import가 없고** 모듈 로드 시점에 DOM 전역을 만지지 않는다.
 *   scripts/test-message-table.js가 transpileModule로 통째로 평가해 node에서 검증하기 때문이다.
 *   DOMParser는 parseClipboardHtml 안에서만 참조한다. import를 추가하면 그 테스트가 깨진다.
 */

/** 표 한 판. 모든 행의 길이가 같다(직사각형으로 정리됨) */
export type TableRows = string[][];

/** 클립보드에서 뽑아낸 표 + 상한에 걸려 잘라낸 양 */
export interface ClipboardTable {
  rows: TableRows;
  truncated: { rows: number; cols: number } | null;
}

/**
 * 붙여넣기 판정 결과.
 *  table = 표만 복사(엑셀·시트·워드) — HTML 기하를 쓰는 기존 경로
 *  mixed = 글과 표를 함께 복사 — text/plain 전체를 정규화한 본문
 */
export type ClipboardPaste =
  | { kind: 'table'; table: ClipboardTable }
  | { kind: 'mixed'; text: string; startsWithTable: boolean; endsWithTable: boolean };

/** 말풍선 본문을 구성하는 조각. 텍스트와 표가 번갈아 나올 수 있다 */
export type MessageBlock =
  | { type: 'text'; text: string }
  | { type: 'table'; rows: TableRows; hasHeader: boolean; align: ('left' | 'right')[] };

export const TABLE_MIN_ROWS = 2;
export const TABLE_MIN_COLS = 2;
export const TABLE_MAX_ROWS = 100;
export const TABLE_MAX_COLS = 30;
/** 셀 하나가 이보다 길면 표로 보지 않는다 — 탭이 우연히 하나 든 긴 문장 2줄을 거른다 */
export const TABLE_MAX_CELL_CHARS = 200;
/**
 * 붙여넣기 1회로 넣을 수 있는 TSV 총 길이.
 * 서버 MAX_MESSAGE_CHARS(기본 10,000)에 닿기 전에 클라이언트에서 막는다 — 소켓 message:error가
 * 도착할 시점엔 ChatWindow가 이미 입력창을 비운 뒤라 붙여넣은 표가 통째로 사라진다.
 * ⚠ "메시지 길이 상한"이 아니라 붙여넣기 1회의 상한이라 서버값과 동기화할 의무가 없다.
 */
export const TABLE_MAX_CHARS = 8000;

/** 병적으로 긴 본문은 렌더 탐지를 아예 건너뛴다 */
const MAX_SCAN_CHARS = 20000;

/**
 * 셀 안의 탭·개행·연속 공백을 공백 하나로 접는다.
 *
 * 저장 포맷이 **이스케이프 없는 단순 TSV**라서 셀 안에 탭·개행이 남으면 격자가 영구히 깨진다.
 * 셀 안 줄바꿈(엑셀 Alt+Enter)이 공백이 되는 것은 의도된 손실이다 — 대안(따옴표 이스케이프)은
 * 구버전 클라이언트에서 따옴표 잡음으로 보여 폴백 원칙에 어긋난다. 도움말에 명시한다.
 *
 * JS의 \s는 NBSP(U+00A0)를 포함하므로 엑셀이 넣는 줄바꿈 방지 공백도 여기서 정리된다.
 */
export function normalizeCell(raw: string): string {
  return String(raw)
    .replace(/\u200b/g, '') // 구글시트가 섞는 zero-width
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * 셀 배열 → 저장 문자열. normalizeCell 불변식 덕에 이스케이프가 필요 없다.
 *
 * ⚠ 1열 표는 탭이 하나도 없어 그냥 여러 줄 평문과 구별할 수 없다. 그래서 각 줄 끝에 탭 하나를
 *   붙여 "오른쪽 전부-빈 열"로 부호화한다 — finalizeRows가 그 열을 잘라내고 looksLikeTable이
 *   '오른쪽만 잘렸을 때'에 한해 1열을 허용한다. 저장 형식은 여전히 순수 평문 TSV라
 *   서버·DB·소켓 계약은 그대로 0이고, 구버전 클라이언트에는 줄 끝 탭이 보이지 않게 남는다.
 */
export function rowsToTsv(rows: TableRows): string {
  const singleColumn = rows.length > 0 && rows[0].length === 1;
  return rows.map(r => (singleColumn ? r[0] + '\t' : r.join('\t'))).join('\n');
}

/** 블록 배열 → 본문 문자열. getMessageBlocks의 역함수 — 컴포저가 셀을 고친 뒤 되돌릴 때 쓴다 */
export function blocksToText(blocks: MessageBlock[]): string {
  return blocks.map(b => (b.type === 'text' ? b.text : rowsToTsv(b.rows))).join('\n');
}

// ── text/plain (TSV) ────────────────────────────────────────────────────────

/**
 * 탭 구분 텍스트를 격자로 읽는다.
 *
 * 엑셀은 셀에 개행·탭·따옴표가 있으면 그 셀을 "..."로 감싸고 내부 "를 ""로 이스케이프한다.
 * 단순 split('\n')으로는 셀 안 개행에서 행이 잘리므로 상태 기계로 읽는다.
 */
export function parseDelimited(text: string, delim = '\t'): TableRows | null {
  const s = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;

  while (i < s.length) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        quoted = false; i++; continue;
      }
      field += ch; i++; continue;
    }
    // 따옴표는 '필드 시작'에서만 여는 것으로 본다 — 12"\tb 같은 인치 표기가 깨지지 않는다
    if (ch === '"' && field === '') { quoted = true; i++; continue; }
    if (ch === delim) { row.push(field); field = ''; i++; continue; }
    if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += ch; i++;
  }
  if (quoted) return null; // 따옴표 미종결 = 표가 아니라 그냥 텍스트

  row.push(field);
  rows.push(row);
  return rows.length > 0 ? rows : null;
}

// ── text/html ───────────────────────────────────────────────────────────────

function clampSpan(n: number): number {
  const v = Number.isFinite(n) ? Math.floor(n) : 1;
  // colspan="1000" 같은 값으로 메모리를 밀어붙이는 입력을 막는다
  return Math.min(Math.max(v, 1), TABLE_MAX_COLS);
}

/**
 * 셀 텍스트를 뽑는다. detached 문서에는 레이아웃이 없어 innerText가 textContent와 같아지므로
 * 직접 순회하며 <br>·블록 요소를 개행으로 바꾼다(그 개행은 normalizeCell이 공백으로 접는다).
 */
function cellText(cell: Element): string {
  let out = '';
  const walk = (n: Node) => {
    if (n.nodeType === 3) { out += n.nodeValue || ''; return; }
    if (n.nodeType !== 1) return;
    const tag = (n as Element).tagName;
    if (tag === 'BR') { out += '\n'; return; }
    // 중첩 표는 셀 값으로 끌어들이지 않는다
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'HEAD' || tag === 'TABLE') return;
    const kids = Array.prototype.slice.call(n.childNodes) as Node[];
    for (const c of kids) walk(c);
    if (tag === 'P' || tag === 'DIV' || tag === 'LI' || tag === 'TR') out += '\n';
  };
  walk(cell);
  return normalizeCell(out);
}

/** Outlook·워드는 레이아웃용 1칸 표로 본문을 감싼다 — 자기 소유 셀이 2개 이상인 첫 표를 고른다 */
function pickTable(doc: Document): HTMLTableElement | null {
  const tables = Array.prototype.slice.call(doc.querySelectorAll('table')) as HTMLTableElement[];
  for (const t of tables) {
    const own = (Array.prototype.slice.call(t.querySelectorAll('td,th')) as Element[])
      .filter(c => c.closest('table') === t);
    if (own.length >= 2) return t;
  }
  return tables.length > 0 ? tables[0] : null;
}

/**
 * 표 요소를 격자로 편다.
 *
 * 병합 셀은 **좌상단에 값, 나머지는 빈칸**으로 둔다 — 엑셀이 text/plain으로 내보내는 모양과
 * 똑같다. 두 경로(HTML/TSV)가 같은 결과를 내야 어느 쪽이 걸렸느냐에 따라 결과가 달라지지 않는다.
 */
function tableToGrid(table: HTMLTableElement): TableRows {
  const trs = (Array.prototype.slice.call(table.querySelectorAll('tr')) as HTMLElement[])
    .filter(tr => tr.closest('table') === table);

  const grid: string[][] = [];
  const carry = new Map<number, { left: number }>(); // 아래로 내려오는 rowspan 잔량
  let budget = 20000; // 총 셀 예산 (병적인 HTML 방어)

  for (const tr of trs.slice(0, TABLE_MAX_ROWS * 2)) {
    const out: string[] = [];
    let col = 0;
    const drain = () => {
      while (carry.has(col)) {
        const c = carry.get(col) as { left: number };
        out[col] = '';
        c.left -= 1;
        if (c.left <= 0) carry.delete(col);
        col++;
      }
    };
    drain();

    const kids = Array.prototype.slice.call(tr.children) as Element[];
    for (const cell of kids) {
      if (cell.tagName !== 'TD' && cell.tagName !== 'TH') continue;
      budget -= 1;
      if (budget < 0) break;
      const text = cellText(cell);
      const cs = clampSpan((cell as HTMLTableCellElement).colSpan);
      const rs = clampSpan((cell as HTMLTableCellElement).rowSpan);
      for (let k = 0; k < cs; k++) {
        out[col] = k === 0 ? text : '';
        if (rs > 1) carry.set(col, { left: rs - 1 });
        col++;
        drain();
      }
    }
    grid.push(out);
    if (grid.length >= TABLE_MAX_ROWS * 2) break;
  }

  const width = grid.reduce((m, r) => Math.max(m, r.length), 0);
  return grid.map(r => {
    const filled: string[] = [];
    for (let j = 0; j < width; j++) filled.push(r[j] === undefined ? '' : r[j]);
    return filled;
  });
}

/**
 * 고른 표 바깥에 글자가 하나라도 있는가.
 *
 * 엑셀(본문 = 표 하나, <style>은 head) · 구글 시트(body 안 <style>) · 워드(표 뒤 &nbsp; 문단) ·
 * 조각 경계 주석(<!--StartFragment-->)은 전부 "글자 없음"이 나와야 기존 표 경로를 탄다.
 * NBSP는 normalizeCell의 \s가 접어 빈 값이 된다.
 */
function hasTextOutside(root: Node, table: Element): boolean {
  let found = false;
  const walk = (n: Node) => {
    if (found || n === table) return;
    if (n.nodeType === 3) {
      if (normalizeCell(n.nodeValue || '') !== '') found = true;
      return;
    }
    if (n.nodeType !== 1 && n.nodeType !== 9 && n.nodeType !== 11) return;
    const tag = n.nodeType === 1 ? (n as Element).tagName : '';
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'HEAD' || tag === 'TEMPLATE' || tag === 'TITLE') return;
    const kids = Array.prototype.slice.call(n.childNodes) as Node[];
    for (const c of kids) walk(c);
  };
  walk(root);
  return found;
}

/**
 * text/html에서 첫 번째 표를 뽑고, 그 표 밖에도 글이 있는지 알려준다.
 *
 * DOMParser가 만드는 문서는 브라우징 컨텍스트가 없어 <script>가 실행되지 않고 리소스도
 * 요청되지 않는다(<img onerror> 포함). 게다가 여기서는 파싱한 노드를 살아 있는 문서에 넣지 않고
 * textContent만 읽으며, 최종 출력이 string[][]이라 React가 텍스트로 이스케이프해 렌더한다.
 * ⚠ 이 파일 어디에서도 innerHTML / dangerouslySetInnerHTML을 쓰지 않는다.
 */
function parseClipboardHtml(html: string): { rows: TableRows; hasOutsideText: boolean } | null {
  if (!html || !/<table[\s>]/i.test(html)) return null;
  if (typeof DOMParser === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = pickTable(doc);
    if (!table) return null;
    return { rows: tableToGrid(table), hasOutsideText: hasTextOutside(doc.body || doc, table) };
  } catch {
    return null;
  }
}

/** text/html에서 첫 번째 표만 뽑는다 */
export function htmlToRows(html: string): TableRows | null {
  const parsed = parseClipboardHtml(html);
  return parsed ? parsed.rows : null;
}

// ── 격자 정리 + 공용 판정 ───────────────────────────────────────────────────

/**
 * finalizeRows의 결과 + **가장자리 빈 열을 어느 쪽에서 잘랐는지**.
 *
 * 이 정보가 없으면 1열을 허용할 수 없다. 아래 셋은 정리 후 격자 모양이 완전히 같아진다:
 *   'a\t'  / 'b\t'          (1열 표)          → [[a],[b]]   오른쪽만 잘림
 *   '\tfoo' / '\tbar'       (탭 들여쓴 코드)   → [[foo],[bar]] 왼쪽만 잘림
 *   'foo' / 'bar'           (그냥 여러 줄)     → [[foo],[bar]] 안 잘림
 * 결과만 보고는 구별할 수 없으므로 잘라낸 쪽이 유일한 단서다.
 */
export interface FinalizedRows {
  rows: TableRows;
  trimmedLeft: number;
  trimmedRight: number;
}

/** 직사각형화 + 셀 정규화 + 가장자리의 전부-빈 행/열 제거 (잘라낸 열 수까지 보고) */
export function finalizeRowsInfo(raw: TableRows): FinalizedRows | null {
  let rows = raw.map(r => r.map(normalizeCell));
  const width = rows.reduce((m, r) => Math.max(m, r.length), 0);
  if (width === 0) return null;
  rows = rows.map(r => {
    const filled: string[] = [];
    for (let j = 0; j < width; j++) filled.push(r[j] === undefined ? '' : r[j]);
    return filled;
  });

  while (rows.length > 0 && rows[rows.length - 1].every(c => c === '')) rows.pop();
  while (rows.length > 0 && rows[0].every(c => c === '')) rows.shift();
  if (rows.length === 0) return null;

  // ★ 탭 들여쓰기 코드가 여기서 죽는다: '\tconst a=1' 두 줄은 0열이 전부 비어
  //   잘려 나가고 1열만 남아 looksLikeTable의 최소 열 수를 통과하지 못한다.
  //   덤으로 A열이 빈 채 복사한 엑셀 범위도 정상적인 표로 살아난다.
  const blankCol = (j: number) => rows.every(r => r[j] === '');
  let left = 0;
  let right = width - 1;
  while (left <= right && blankCol(left)) left++;
  while (right >= left && blankCol(right)) right--;
  if (right < left) return null;
  rows = rows.map(r => r.slice(left, right + 1));

  return rows.length > 0
    ? { rows, trimmedLeft: left, trimmedRight: width - 1 - right }
    : null;
}

/** 잘라낸 쪽이 필요 없는 호출부용 (기존 시그니처 유지) */
export function finalizeRows(raw: TableRows): TableRows | null {
  const info = finalizeRowsInfo(raw);
  return info ? info.rows : null;
}

/**
 * 정리 결과가 1열을 허용해도 되는 모양인가 — 오른쪽 빈 열이 **정확히 하나** 잘렸을 때다.
 * 붙여넣기와 렌더가 이 계산을 공유해야 대칭이 깨지지 않는다.
 *
 * ⚠ `> 0`이 아니라 `=== 1`인 이유: rowsToTsv가 부호화하는 후행 탭은 언제나 한 개다.
 *   `a\t\t\t` 처럼 탭이 여러 개인 것은 성긴 탭 정렬 텍스트라 R7이 걸러야 하는 쪽이다.
 *   (붙여넣기 때 빈 열이 두 개인 엑셀 범위가 와도 저장은 탭 하나로 정규화되므로 대칭이 유지된다)
 */
export function allowsSingleColumn(info: FinalizedRows): boolean {
  return info.trimmedRight === 1 && info.trimmedLeft === 0;
}

/**
 * ★ 붙여넣기와 렌더가 공유하는 유일한 판정. 여기가 곧 정책이다.
 *
 * 규칙과 그것이 막는 것:
 *   R1 행 2~100          한 줄짜리 '키\t값', 탭 하나 섞인 문장
 *   R2 모든 행 열 수 동일  들여쓰기 깊이가 변하는 코드
 *   R3 열 2~30           탭 수십 개짜리 정렬 로그·ASCII 아트
 *   R4 첫 열이 전부 비지 않음 (finalizeRows의 가장자리 정리와 중복이지만 방어)
 *   R5 내부 전부-빈 열 없음  '1. 개요\t\t\t3' 같은 탭 정렬 목차
 *   R6 셀 200자 이하       탭이 우연히 든 긴 문장 2줄
 *   R7 비지 않은 셀 50% 이상 연속 탭이 만든 성긴 정렬 텍스트
 *
 * ⚠ allowSingleColumn은 R3의 하한만 1로 낮춘다. 호출부가 임의로 켜면 안 되고
 *   `allowsSingleColumn(finalizeRowsInfo(...))`(= 오른쪽 빈 열만 잘림) 또는 HTML 표 출처일 때만
 *   켠다. 그냥 하한을 1로 두면 탭으로 들여쓴 코드가 왼쪽 빈 열이 잘려 표로 오인된다.
 */
export function looksLikeTable(rows: TableRows, allowSingleColumn = false): boolean {
  const r = rows.length;
  if (r < TABLE_MIN_ROWS || r > TABLE_MAX_ROWS) return false;
  const c = rows[0].length;
  if (c < (allowSingleColumn ? 1 : TABLE_MIN_COLS) || c > TABLE_MAX_COLS) return false;
  if (!rows.every(row => row.length === c)) return false;
  if (rows.every(row => row[0] === '')) return false;
  for (let j = 0; j < c; j++) {
    if (rows.every(row => row[j] === '')) return false;
  }
  let nonEmpty = 0;
  for (const row of rows) {
    for (const cell of row) {
      if (cell.length > TABLE_MAX_CELL_CHARS) return false;
      if (cell !== '') nonEmpty++;
    }
  }
  return nonEmpty * 2 >= r * c;
}

// ── 붙여넣기 진입점 ─────────────────────────────────────────────────────────

function clampTable(rows: TableRows): ClipboardTable | null {
  const cutRows = Math.max(0, rows.length - TABLE_MAX_ROWS);
  const cutCols = Math.max(0, rows[0].length - TABLE_MAX_COLS);
  let out = rows.slice(0, TABLE_MAX_ROWS).map(r => r.slice(0, TABLE_MAX_COLS));

  let byChars = 0;
  while (out.length > TABLE_MIN_ROWS && rowsToTsv(out).length > TABLE_MAX_CHARS) {
    out = out.slice(0, out.length - 1);
    byChars++;
  }
  // 최소 행수로도 상한을 못 맞추면 표로 만들지 않는다(기본 텍스트 붙여넣기로 넘어간다)
  if (rowsToTsv(out).length > TABLE_MAX_CHARS) return null;

  const droppedRows = cutRows + byChars;
  const truncated = droppedRows > 0 || cutCols > 0 ? { rows: droppedRows, cols: cutCols } : null;
  return { rows: out, truncated };
}

/**
 * 클립보드에서 표를 뽑는다. 표가 아니면 null → 호출부는 preventDefault 없이 반환해
 * 브라우저 기본 텍스트 붙여넣기에 맡긴다.
 *
 * HTML을 먼저 본다 — colspan/rowspan 기하를 정확히 아는 유일한 경로다.
 * 결과가 빈약(1칸)하면 text/plain의 TSV로 내려간다.
 *
 * ⚠ 표 밖의 글은 보지 않는다. 붙여넣기는 이 함수가 아니라 extractClipboardPaste로 들어와야 한다
 *   (글과 표가 섞인 클립보드를 여기로 보내면 본문이 버려진다).
 */
export function extractClipboardTable(html: string, plain: string): ClipboardTable | null {
  return tableFromSources(htmlToRows(html), plain);
}

function tableFromSources(fromHtml: TableRows | null, plain: string): ClipboardTable | null {
  // 1열도 HTML 표에서 왔으면 받는다 — 엑셀에서 한 열만 복사하면 text/plain에 탭이 아예 없어
  // 이 경로 말고는 표라는 사실을 알 길이 없다. 대신 text/plain만 있는 여러 줄 텍스트는
  // 아래 looksLikeTable이 1열을 거부하므로 예전처럼 그냥 텍스트로 남는다.
  // 오탐(워드·아웃룩의 1열 레이아웃 표)은 컴포저 격자에 그대로 보이고 X로 지울 수 있어
  // 보내기 전에 사용자가 되돌릴 수 있다.
  // 1×1은 워드·아웃룩의 레이아웃 래퍼라 예전처럼 text/plain으로 내려간다
  const htmlUsable = !!fromHtml && (fromHtml.length > 1 || fromHtml[0].length > 1);
  const candidate = htmlUsable ? (fromHtml as TableRows) : parseDelimited(plain);
  if (!candidate) return null;

  const info = finalizeRowsInfo(candidate);
  if (!info) return null;

  const clamped = clampTable(info.rows);
  if (!clamped) return null;
  // ★ 렌더와 동일한 판정을 통과하지 못하면 표로 만들지 않는다
  if (!looksLikeTable(clamped.rows, htmlUsable || allowsSingleColumn(info))) return null;
  return clamped;
}

/**
 * ★ 붙여넣기의 진입점. 표만 복사했는지, 글과 표를 함께 복사했는지를 가른다.
 *
 * ⚠ 글과 표가 섞인 클립보드(웹 페이지·AI 답변을 드래그 복사)에서 표만 뽑으면 handlePaste가
 *   기본 붙여넣기를 막으므로 **나머지 본문이 조용히 사라진다**(실제 신고). 그래서 HTML의 표 밖에
 *   글자가 있으면 표 경로를 타지 않고 text/plain 전체를 쓴다 — 브라우저는 선택 영역의 표를 셀=탭,
 *   행=개행으로 직렬화하므로 렌더 판정이 그 구간을 다시 표로 살린다.
 */
export function extractClipboardPaste(html: string, plain: string): ClipboardPaste | null {
  const parsed = parseClipboardHtml(html);
  if (parsed && parsed.hasOutsideText) return mixedPaste(plain);
  const table = tableFromSources(parsed ? parsed.rows : null, plain);
  return table ? { kind: 'table', table } : null;
}

/**
 * 글 + 표 클립보드. 본문을 통째로 정규화해 넣는다.
 * 표 구간을 하나도 못 찾으면(표를 탭 없이 직렬화한 출처) null → 브라우저 기본 붙여넣기에 맡긴다.
 * 표가 평문이 되더라도 본문이 사라지는 것보다 낫다.
 */
export function mixedPaste(plain: string): ClipboardPaste | null {
  const text = normalizeMessageText(String(plain).replace(/\r\n?/g, '\n'));
  const blocks = getComposerBlocks(text);
  if (!blocks) return null;
  return {
    kind: 'mixed',
    text,
    startsWithTable: blocks[0].type === 'table',
    endsWithTable: blocks[blocks.length - 1].type === 'table',
  };
}

// ── 렌더 진입점 ─────────────────────────────────────────────────────────────

const NUMERIC = /^[-+]?[₩$]?[\d,]+(\.\d+)?%?$/;

/**
 * 첫 행에 빈 칸도 숫자도 없고 3행 이상일 때만 머리글로 본다.
 * 1열은 제외한다 — 이름만 나열한 목록의 첫 줄이 머리글로 굵게 나오면 값이 하나 사라진 것처럼 읽힌다.
 */
function guessHeader(rows: TableRows): boolean {
  if (rows.length < 3 || rows[0].length < 2) return false;
  return rows[0].every(c => c !== '' && !NUMERIC.test(c));
}

/** 머리글을 뺀 값이 전부 숫자인 열은 우측 정렬 — 표가 표처럼 읽히는 결정적 디테일 */
function guessAlign(rows: TableRows): ('left' | 'right')[] {
  const body = guessHeader(rows) ? rows.slice(1) : rows;
  return rows[0].map((_, j) => {
    const vals = body.map(r => r[j]).filter(v => v !== '');
    return vals.length > 0 && vals.every(v => NUMERIC.test(v)) ? 'right' : 'left';
  });
}

/** 본문 평문을 텍스트/표 블록으로 나눈다. 표가 하나도 없으면 null(= 기존 렌더 경로 그대로) */
export function splitMessageBlocks(text: string): MessageBlock[] | null {
  const lines = text.split('\n');
  const colsOf = lines.map(l => (l.indexOf('\t') === -1 ? 0 : l.split('\t').length));
  const blocks: MessageBlock[] = [];
  let textStart = 0;
  let i = 0;
  let found = false;

  while (i < lines.length) {
    if (colsOf[i] < TABLE_MIN_COLS) { i++; continue; }
    let j = i + 1;
    while (j < lines.length && colsOf[j] === colsOf[i]) j++; // 열 개수가 같은 최대 연속 구간
    const info = finalizeRowsInfo(lines.slice(i, j).map(l => l.split('\t')));
    if (info && looksLikeTable(info.rows, allowsSingleColumn(info))) {
      const rows = info.rows;
      if (i > textStart) blocks.push({ type: 'text', text: lines.slice(textStart, i).join('\n') });
      blocks.push({ type: 'table', rows, hasHeader: guessHeader(rows), align: guessAlign(rows) });
      textStart = j;
      found = true;
    }
    i = j;
  }

  if (!found) return null;
  if (textStart < lines.length) blocks.push({ type: 'text', text: lines.slice(textStart).join('\n') });
  return blocks;
}

// 렌더 체인은 검색어가 바뀔 때마다 화면의 모든 메시지에 대해 다시 돈다(ChatWindow의 memo 계약).
// 결과 참조가 매번 바뀌면 MessageTable의 React.memo가 무력화되므로 모듈 스코프 LRU로 고정한다.
const CACHE_LIMIT = 300;
const cache = new Map<string, MessageBlock[] | null>();

/** ★ 렌더 경로의 유일한 진입점 */
export function getMessageBlocks(text: string): MessageBlock[] | null {
  // 탭 없는 메시지(대부분)는 Map을 건드리지도 않는다 — indexOf 한 번이 전부다.
  // 이게 없으면 캐시가 일반 메시지로 가득 차 정작 표가 밀려난다.
  if (!text || text.length > MAX_SCAN_CHARS || text.indexOf('\t') === -1) return null;

  if (cache.has(text)) {
    const hit = cache.get(text) as MessageBlock[] | null;
    cache.delete(text);
    cache.set(text, hit); // 재삽입 = 최근성 갱신(LRU)
    return hit;
  }
  const val = splitMessageBlocks(text);
  cache.set(text, val);
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value; // Map은 삽입 순서를 보장한다
    if (oldest !== undefined) cache.delete(oldest);
  }
  return val;
}

/** 테스트 전용 */
export function __cacheSize(): number {
  return cache.size;
}

// ── 컴포저 전용 (입력창 격자 편집) ──────────────────────────────────────────

/**
 * 컴포저 전용 진입점. 판정은 getMessageBlocks와 **완전히 같고** LRU만 쓰지 않는다.
 *
 * ⚠ 컴포저에서 getMessageBlocks를 쓰면 안 된다. 표가 있는 상태의 키 입력 한 글자마다
 *   새 문자열 키가 캐시에 쌓여 300칸을 금세 밀어내고, 그러면 말풍선 표의 rows 참조가
 *   매번 새로 만들어져 MessageTable의 React.memo가 통째로 무력화된다.
 */
export function getComposerBlocks(text: string): MessageBlock[] | null {
  if (!text || text.length > MAX_SCAN_CHARS || text.indexOf('\t') === -1) return null;
  return splitMessageBlocks(text);
}

/** 블록 하나의 직렬형만 갈아끼운다 (셀 편집·텍스트 블록 입력) */
export function replaceBlockText(text: string, index: number, replacement: string): string {
  const blocks = getComposerBlocks(text);
  if (!blocks || index < 0 || index >= blocks.length) return text;
  return blocks
    .map((b, i) => (i === index ? replacement : b.type === 'text' ? b.text : rowsToTsv(b.rows)))
    .join('\n');
}

/** 표 카드 X — 그 블록만 빼고 재결합 */
export function removeBlock(text: string, index: number): string {
  const blocks = getComposerBlocks(text);
  if (!blocks || index < 0 || index >= blocks.length) return text;
  return blocksToText(blocks.filter((_, i) => i !== index));
}

/**
 * 셀 하나를 고친 새 격자. 저장 포맷 불변식(탭·개행 없음, 200자 이하)을 여기서 강제한다.
 * trim은 하지 않는다 — 타이핑 중에 뒤 공백을 지우면 낱말 사이에 공백을 칠 수 없다.
 */
export function setCell(rows: TableRows, r: number, c: number, value: string): TableRows {
  const clean = String(value)
    .replace(/\u200b/g, '')
    .replace(/[\t\r\n]+/g, ' ')
    .slice(0, TABLE_MAX_CELL_CHARS);
  return rows.map((row, i) => (i === r ? row.map((cell, j) => (j === c ? clean : cell)) : row));
}

/**
 * 본문을 "블록으로 나눴다 합쳐도 그대로인" 모양으로 한 번 정리한다.
 *
 * 컴포저는 매 렌더 문자열을 블록으로 다시 나누므로, 나누고 합치는 것이 항등이 아니면
 * 타이핑한 글자가 되돌아가 보인다. finalizeRows가 셀을 정규화하고 가장자리 빈 행·열을
 * 잘라내므로 처음 들어온 문자열은 대체로 항등이 아니다.
 *
 * ⚠ 한 번으로는 부족하다. 정규화로 표의 열 수가 줄면 바로 위 텍스트 줄의 탭 개수와 같아져
 *   다음 스캔에서 그 줄이 표에 흡수될 수 있다. 블록은 합쳐지기만 하고 쪼개지지 않으므로
 *   반복은 반드시 수렴한다.
 */
export function normalizeMessageText(text: string): string {
  if (!text || text.indexOf('\t') === -1) return text; // 표 없는 본문은 바이트 그대로
  let cur = text;
  for (let i = 0; i < 4; i++) {
    const blocks = getComposerBlocks(cur);
    if (!blocks) return cur;
    const next = blocksToText(blocks);
    if (next === cur) return cur;
    cur = next;
  }
  return cur;
}

/**
 * 말풍선 '복사'용 — 1열 표의 부호화 탭을 벗긴다.
 * 안 벗기면 엑셀에 다시 붙일 때 빈 B열이 하나 생긴다.
 */
export function textForClipboard(text: string): string {
  const blocks = getComposerBlocks(text);
  if (!blocks) return text;
  return blocks
    .map(b => (b.type === 'text' ? b.text : b.rows.map(r => r.join('\t')).join('\n')))
    .join('\n');
}
